<template>
  <div
    class="rounded-lg flex-col justify-start items-start gap-2 inline-flex border-gray-300 w-full"
  >
    <div
      class="self-stretch bg-gray-200 rounded-lg flex-col justify-start items-center flex"
    >
      <div
        class="self-stretch pb-1 pt-2 bg-gray-200 rounded-t-lg flex-col justify-start items-center flex"
      >
        <div
          class="self-stretch pl-3 pr-2 justify-center items-start inline-flex"
        >
          <div
            class="grow shrink basis-0 h-5 justify-start items-center gap-2 flex"
          >
            <div
              class="grow shrink basis-0 h-4 text-gray-600 text-xs font-semibold uppercase"
            >
              Appointment #{{ appointmentNumber }}
            </div>
            <div class="px-1.5 py-1 rounded justify-start items-center flex">
              <div class="text-gray-600 text-xs font-normal leading-3">
                Created {{ createdDate }}
              </div>
              <div class="relative cursor-pointer shrink-0 pl-1">
                <Tooltip>
                  <template #trigger>
                    <button type="button">
                      <svg
                        width="10"
                        height="10"
                        viewBox="0 0 10 10"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g clip-path="url(#clip0_2051_32766)">
                          <path
                            d="M5 0C4.0111 0 3.0444 0.293245 2.22215 0.842652C1.3999 1.39206 0.759043 2.17295 0.380605 3.08658C0.00216642 4.00021 -0.0968502 5.00555 0.0960758 5.97545C0.289002 6.94535 0.765206 7.83627 1.46447 8.53553C2.16373 9.23479 3.05465 9.711 4.02455 9.90392C4.99445 10.0969 5.99979 9.99783 6.91342 9.6194C7.82705 9.24096 8.60794 8.60009 9.15735 7.77785C9.70675 6.9556 10 5.9889 10 5C9.99854 3.67436 9.47129 2.40344 8.53393 1.46607C7.59656 0.528707 6.32564 0.0014558 5 0ZM4.75 2C4.89834 2 5.04334 2.04399 5.16668 2.1264C5.29002 2.20881 5.38615 2.32594 5.44291 2.46299C5.49968 2.60003 5.51453 2.75083 5.48559 2.89632C5.45665 3.0418 5.38522 3.17544 5.28033 3.28033C5.17544 3.38522 5.04181 3.45665 4.89632 3.48559C4.75083 3.51453 4.60003 3.49967 4.46299 3.44291C4.32594 3.38614 4.20881 3.29001 4.1264 3.16668C4.04399 3.04334 4 2.89834 4 2.75C4 2.55109 4.07902 2.36032 4.21967 2.21967C4.36032 2.07902 4.55109 2 4.75 2ZM6 7.5H4C3.86739 7.5 3.74022 7.44732 3.64645 7.35355C3.55268 7.25978 3.5 7.13261 3.5 7C3.5 6.86739 3.55268 6.74021 3.64645 6.64644C3.74022 6.55268 3.86739 6.5 4 6.5H4.5V5H4C3.86739 5 3.74022 4.94732 3.64645 4.85355C3.55268 4.75978 3.5 4.63261 3.5 4.5C3.5 4.36739 3.55268 4.24021 3.64645 4.14645C3.74022 4.05268 3.86739 4 4 4H5C5.13261 4 5.25979 4.05268 5.35355 4.14645C5.44732 4.24021 5.5 4.36739 5.5 4.5V6.5H6C6.13261 6.5 6.25979 6.55268 6.35355 6.64644C6.44732 6.74021 6.5 6.86739 6.5 7C6.5 7.13261 6.44732 7.25978 6.35355 7.35355C6.25979 7.44732 6.13261 7.5 6 7.5Z"
                            fill="#4B5563"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_2051_32766">
                            <rect width="10" height="10" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                    </button>
                  </template>
                  <template #tooltip>
                    <div
                      class="w-max min-w-[120px] text-sm font-medium text-white transition-opacity duration-300 dark:bg-gray-700"
                    >
                      <span
                        class="block text-gray-400 leading-tight text-center"
                        >Created by</span
                      >
                      <span class="block leading-tight text-center">
                        {{ createdBy }}
                      </span>
                    </div>
                  </template>
                </Tooltip>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="self-stretch flex-col justify-start items-start flex">
        <div class="self-stretch justify-start items-start inline-flex">
          <!-- Fixed top row boxes with consistent height -->
          <Tooltip
            class="w-full"
            :options="scheduledTooltipOptions"
            :disabled="!scheduledTimeEditable"
          >
            <template #trigger>
              <div
                :class="[
                  ' w-full border-b-0 box-border relative pl-3 pr-5 pb-2 bg-white rounded-tl-lg border-t border-l border-gray-300 flex-col justify-start items-start gap-2 inline-flex',
                  {
                    'border-primary-700 !bg-blue-100': scheduledActive
                  },
                  {
                    'hover:bg-blue-100 hover:cursor-pointer':
                      scheduledTimeEditable
                  }
                ]"
                style="
                  border-top-left-radius: 10px;
                  height: 61px;
                  box-sizing: border-box;
                "
              >
                <div
                  class="absolute inset-0 hover:border-blue-500 hover:border-b pointer-events-none rounded-tl-lg"
                  style="z-index: 1"
                ></div>
                <div
                  class="self-stretch justify-start items-center gap-2 inline-flex"
                >
                  <div
                    class="h-9 flex-col justify-start items-start gap-0.5 inline-flex"
                  >
                    <div
                      class="pt-2 text-gray-500 text-xs font-normal leading-3"
                    >
                      Scheduled for
                    </div>
                    <div
                      class="text-gray-900 text-sm font-normal whitespace-nowrap overflow-visible block"
                    >
                      {{
                        `${scheduledStartDate} - ${scheduledStartTime} to ${
                          scheduledEnd || "N/A"
                        }`
                      }}
                    </div>
                  </div>
                </div>
              </div>
            </template>
            <template #tooltip>
              <div class="shadow rounded-lg">
                <slot name="scheduledTimeTooltip"></slot>
              </div>
            </template>
          </Tooltip>

          <Tooltip
            class="w-full"
            :options="durationTooltipOptions"
            :disabled="!durationEditable"
          >
            <template #trigger>
              <div
                :class="[
                  'border-b-0 box-border w-full relative rounded-tr-lg grow shrink basis-0 pl-3 pr-5 pb-2 bg-white border-t border-l border-r border-gray-300 flex-col justify-start items-start gap-2 inline-flex',
                  {
                    'border-primary-700 !bg-blue-100': durationActive
                  },
                  {
                    'hover:bg-blue-100 hover:cursor-pointer': durationEditable
                  }
                ]"
                style="
                  border-top-right-radius: 10px;
                  height: 61px;
                  box-sizing: border-box;
                "
              >
                <div
                  class="absolute inset-0 hover:border-blue-500 hover:border-b pointer-events-none rounded-tr-lg"
                  style="z-index: 1"
                ></div>
                <div
                  class="self-stretch justify-start items-center gap-2 inline-flex"
                >
                  <div
                    class="h-9 flex-col justify-start items-start gap-0.5 inline-flex"
                  >
                    <div
                      class="pt-2 text-gray-500 text-xs font-normal leading-3"
                    >
                      Estimated Duration
                    </div>
                    <div class="text-gray-900 text-sm font-normal">
                      {{ estimatedDuration }}
                    </div>
                  </div>
                </div>
              </div>
            </template>
            <template #tooltip>
              <div class="shadow rounded-lg">
                <slot name="estimatedDurationTooltip"></slot>
              </div>
            </template>
          </Tooltip>
        </div>
        <div class="self-stretch justify-center items-center inline-flex">
          <!-- Customer section with fixed height -->
          <div
            class="border-b-0 box-border relative grow shrink basis-0 px-3 py-2 bg-white border-s border-t border-gray-300 flex-col justify-start items-start gap-2 inline-flex"
            style="
              border-right: 1px solid #d1d5db;
              height: 63px;
              box-sizing: border-box;
            "
          >
            <div
              class="absolute inset-0 hover:border-blue-500 hover:border-b pointer-events-none"
              style="z-index: 1"
            ></div>
            <div
              class="self-stretch justify-start items-center gap-2 inline-flex"
            >
              <div
                class="grow shrink basis-0 flex-col justify-start items-start gap-0.5 inline-flex"
              >
                <div class="text-gray-500 text-xs font-normal leading-3">
                  Customer
                </div>
                <div
                  class="self-stretch justify-start items-center gap-1.5 inline-flex"
                >
                  <div
                    class="grow shrink basis-0 h-5 justify-between items-center flex"
                  >
                    <div class="justify-start items-center gap-0.5 flex pt-1">
                      <!-- Child indicator with Flowbite tooltip -->
                      <div v-if="child" class="cursor-pointer">
                        <button
                          data-tooltip-target="parent-tooltip"
                          data-tooltip-placement="top"
                          type="button"
                        >
                          <div class="mt-2.5">
                            <svg
                              width="10"
                              height="14"
                              viewBox="0 0 10 14"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M9.24968 10.7497C9.24968 10.8823 9.197 11.0094 9.10324 11.1032C9.00948 11.197 8.8823 11.2496 8.7497 11.2496H4.7499C4.61729 11.2496 4.49012 11.197 4.39636 11.1032C4.30259 11.0094 4.24992 10.8823 4.24992 10.7497V3.95683L2.10375 6.10363C2.00993 6.19744 1.88269 6.25015 1.75001 6.25015C1.61734 6.25015 1.49009 6.19744 1.39628 6.10363C1.30246 6.00981 1.24976 5.88257 1.24976 5.74989C1.24976 5.61721 1.30246 5.48997 1.39628 5.39615L4.39616 2.39627C4.44259 2.34979 4.49774 2.31291 4.55843 2.28775C4.61913 2.26258 4.68419 2.24963 4.7499 2.24963C4.8156 2.24963 4.88066 2.26258 4.94136 2.28775C5.00206 2.31291 5.0572 2.34979 5.10363 2.39627L8.10351 5.39615C8.14997 5.44261 8.18682 5.49775 8.21196 5.55845C8.2371 5.61914 8.25004 5.6842 8.25004 5.74989C8.25004 5.81558 8.2371 5.88064 8.21196 5.94133C8.18682 6.00202 8.14997 6.05717 8.10351 6.10363C8.05706 6.15008 8.00191 6.18693 7.94122 6.21207C7.88052 6.23721 7.81547 6.25015 7.74978 6.25015C7.68408 6.25015 7.61903 6.23721 7.55834 6.21207C7.49764 6.18693 7.4425 6.15008 7.39604 6.10363L5.24988 3.95683V10.2497H8.7497C8.8823 10.2497 9.00948 10.3023 9.10324 10.3961C9.197 10.4899 9.24968 10.6171 9.24968 10.7497Z"
                                fill="#D1D5DB"
                                stroke="#D1D5DB"
                                stroke-width="0.5"
                              />
                            </svg>
                          </div>
                        </button>
                        <div
                          id="parent-tooltip"
                          role="tooltip"
                          class="absolute z-10 invisible inline-block px-3 py-2 text-sm font-medium text-white transition-opacity duration-300 bg-gray-900 rounded-lg shadow-sm opacity-0 tooltip dark:bg-gray-700"
                        >
                          <p class="text-gray-400">Sub-customer of</p>
                          <p>{{ subCustomerOf }}</p>
                          <div class="tooltip-arrow" data-popper-arrow></div>
                        </div>
                      </div>
                      <!-- Customer name -->
                      <a
                        class="text-blue-600 text-sm font-large leading-none pt-1"
                        :href="userShowPath(customer.customer_id)"
                      >
                        {{ customer.full_name }}
                      </a>
                    </div>
                    <div class="justify-start items-center gap-2.5 flex">
                      <div
                        v-if="overdueCons"
                        class="text-red-600 text-sm font-normal leading-none pt-1"
                      >
                        Overdue {{ overdueCons }}
                      </div>
                      <!-- Membership info tooltip -->
                      <div class="relative cursor-pointer pt-2">
                        <Tooltip v-if="customer.membership">
                          <template #trigger>
                            <button
                              data-tooltip-target="membership-tooltip"
                              data-tooltip-placement="left"
                              type="button"
                            >
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <rect
                                  width="20"
                                  height="20"
                                  rx="10"
                                  fill="#FFE995"
                                />
                                <path
                                  d="M12.8092 11.9331H7.19383C7.10784 11.9331 7.03323 11.8738 7.0139 11.79L6.06235 7.6666C6.02256 7.49421 6.22458 7.36996 6.3605 7.48322L7.97578 8.82929C8.06726 8.90552 8.20591 8.87652 8.25916 8.77001L9.83633 5.61567C9.90438 5.47957 10.0986 5.47957 10.1667 5.61567L11.7438 8.77001C11.7971 8.87652 11.9357 8.90552 12.0272 8.82929L13.6425 7.48322C13.7784 7.36996 13.9804 7.49421 13.9406 7.6666L12.9891 11.79C12.9698 11.8738 12.8951 11.9331 12.8092 11.9331Z"
                                  stroke="#00222E"
                                  stroke-width="0.923302"
                                />
                                <path
                                  d="M7.04358 13.7787H12.9527"
                                  stroke="#00222E"
                                  stroke-width="0.923302"
                                  stroke-linecap="round"
                                />
                              </svg>
                            </button>
                          </template>
                          <template #tooltip>
                            <div
                              id="membership-tooltip"
                              role="tooltip"
                              class="w-max min-w-[120px] px-3 py-2 text-sm font-medium text-white dark:bg-gray-700"
                            >
                              <p class="text-gray-400">Member since</p>
                              <p>
                                {{ formatDateWithTimezone(memberSinceDate) }}
                              </p>
                              <p v-if="expireDate" class="text-gray-400">
                                Exp. {{ formatDateWithTimezone(expireDate) }}
                              </p>
                              <div
                                class="tooltip-arrow"
                                data-popper-arrow
                              ></div>
                            </div>
                          </template>
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="flex items-start justify-start w-full">
          <!-- Customer Tags section -->
          <AppointmentCustomerTags
            :customer="customer"
            @tags-change="emit('refresh-appointment')"
          ></AppointmentCustomerTags>
        </div>
        <div class="flex items-start justify-start w-full">
          <!-- Job Tags section -->
          <AppointmentJobTags
            :job="job"
            @tags-change="emit('refresh-appointment')"
          ></AppointmentJobTags>
        </div>
        <div class="self-stretch justify-center items-center inline-flex">
          <!-- Location section with fixed height and persistent bottom border -->
          <div
            class="box-border relative grow shrink basis-0 px-3 py-2 bg-white border border-gray-300 flex-col justify-start items-start gap-2 inline-flex rounded-bl-lg rounded-br-lg"
            style="
              border-right: 1px solid #d1d5db;
              min-height: 69px;
              box-sizing: border-box;
            "
          >
            <!-- Remove border-b-0 class that was causing the missing bottom border -->
            <div
              class="absolute inset-0 pointer-events-none rounded-bl-lg rounded-br-lg"
              style="z-index: 1"
            ></div>
            <div
              class="self-stretch justify-start items-center gap-2 inline-flex"
            >
              <div
                class="grow shrink basis-0 flex-col justify-start items-start gap-0.5 inline-flex"
              >
                <div class="text-gray-500 text-xs font-normal leading-3">
                  Location
                </div>
                <div
                  class="self-stretch justify-start items-center gap-1.5 inline-flex"
                >
                  <div
                    class="grow shrink basis-0 h-5 justify-between items-center flex"
                  >
                    <div
                      class="text-black-600 text-sm font-large font-semibold leading-none pt-1"
                    >
                      {{ locationLine1 }}
                    </div>
                  </div>
                </div>
                <div class="text-gray-900 text-sm font-normal">
                  {{ locationLine2 }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, defineProps, ref } from "vue";

import Tooltip from "@/components/Tooltip.vue";
import AppointmentCustomerTags from "@/AppointmentDetails/AppointmentCustomerTags.vue";
import AppointmentJobTags from "@/AppointmentDetails/AppointmentJobTags.vue";
import { userShowPath } from "@/mixins/path_helpers";
import { Customer } from "@/typings/customer";
import { Job } from "@/typings/job";
import { Tag } from "@/typings/tag";
const emit = defineEmits(["refresh-appointment"]);
const props = defineProps({
  appointmentNumber: {
    type: [String, Number],
    required: true
  },
  createdDate: {
    type: String,
    required: true
  },
  scheduledStartDate: {
    type: String,
    required: true
  },
  scheduledStartTime: {
    type: String,
    required: true
  },
  scheduledEnd: {
    type: String,
    required: true
  },
  estimatedDuration: {
    type: String,
    required: true
  },
  customer: {
    type: Customer,
    required: true
  },
  job: {
    type: Job,
    required: true
  },
  overdueCons: {
    type: String
  },
  locationLine1: {
    type: String,
    required: true
  },
  locationLine2: {
    type: String,
    required: true
  },
  customerTags: {
    type: Array as () => Tag[],
    required: true
  },
  jobTags: {
    type: Array as () => Tag[],
    required: true
  },
  child: {
    type: Boolean,
    required: false,
    default: false
  },
  createdBy: {
    type: String,
    required: true
  },
  subCustomerOf: {
    type: String,
    required: false
  },
  memberSinceDate: {
    type: String
  },
  expireDate: {
    type: String,
    required: false
  },
  scheduledTimeEditable: {
    type: Boolean,
    default: false
  },
  durationEditable: {
    type: Boolean,
    default: false
  }
});
const durationActive = ref(false);
const scheduledActive = ref(false);

function toggleEstimatedActive() {
  durationActive.value = !durationActive.value;
}

function toggleScheduleActive() {
  scheduledActive.value = !scheduledActive.value;
}

const tooltipOptions = computed(() => ({
  appendTo: document.body,
  interactive: true,
  hideOnClick: true,
  distance: -10,
  arrow: false,
  placement: "bottom",
  trigger: "click",
  theme: "actions-menu light"
}));

const durationTooltipOptions = computed(() => ({
  ...tooltipOptions.value,
  onShow: toggleEstimatedActive,
  onHidden: toggleEstimatedActive
}));

const scheduledTooltipOptions = computed(() => ({
  ...tooltipOptions.value,
  offset: 150,
  onShow: toggleScheduleActive,
  onHidden: toggleScheduleActive
}));
</script>

<style>
/* Add these styles to ensure consistent box sizing */
* {
  box-sizing: border-box;
}
</style>
